import {
  Accordion,
  AccordionDetail,
  AccordionHeader,
  AccordionItem,
  AccordionSubTitle,
  AccordionTitle,
} from "@vds/accordions";
import { ButtonIcon } from "@vds/button-icons";
import { Icon } from "@vds/icons";
import { Loader } from "@vds/loaders";
import { RadioButton, RadioButtonGroup } from "@vds/radio-buttons";
import { DropdownSelect } from "@vds/selects";
import { Tab, Tabs } from "@vds/tabs";
import { Body, Typography } from "@vds/typography";
import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import ParkingField from "../../../global/components/ParkingCapacityField";
import { SELECT_ROI_CONSTANTS, ROI_REDUX_PROPS, LOCATION_TYPES } from "../../../global/constants/constants";
import {
  CVAPP_PARKINGOCCUPANCYEE,
  CVAPP_PARKINGOCCUPANCYOA,
  CV_APP_TYPE_EVENT,
  CV_APP_TYPE_EE,
  CV_APP_TYPE_OA
} from "../../../global/constants/cvappConstants";
import { transformPolygonData } from "../../../global/utility/roiUtils";
import { ImageDownloadMedia } from "../../../model/mediaService";
import { getTagHierarchy } from "../../../model/reportService";
import DisplayCanvas from "../../../polygonLib/displayPolygonLib/displayCanvas";
import { resetRuleValue, updateRuleValue } from "../../../store/actions";
import "../bulkrule.css";
import RuleListModal from "./RuleListModal";
import { CREATERULE_TEST_IDS } from "./createRuleConstants";
import { LightText } from "../../../features/camerOnboarding/discoveredCameraOnboarding/styles";

const SelectROI = (props) => {
  const { handleRoiParentId } = props;
  const dispatch = useDispatch();
  const [floorList, setFloorList] = useState([]);
  const [siteRoiList, setSiteRoiList] = useState([]);
  const [floorDataArr, setFloorDataArr] = useState([]);
  const [roiList, setRoiList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedRoi, setSelectedRoi] = useState(null);
  const { activeSession } = useSelector(
    (state) => state.sessionReducer.activeSession
  );
  const [trigger, setTrigger] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [floorImageLoading, setFloorImageLoading] = useState(false);
  const {
    siteSelected,
    floorSelected,
    roiSelected,
    roiDatas,
    appSelected,
    cvAppRois,
    areaSelected,
    subAreaSelected,
    selAppType,
    selectedLocation,
    roiImages,
    floorData,
    selectedTab = 0,
    floorRoiList,
    sitesRoiList,
    selectArea = false,
    selectSubArea = false,
    checkedAreaRoi = false,
    checkedSubAreaRoi = false,
  } = useSelector((state) => state.ruleReducer);
  const { fovList } = useSelector((state) => state?.deviceReducer);
  const tagAllLists = useSelector((state) => state?.sitesReducer.tagIdList);
  const { siteTagList } = useSelector((state) => state?.sitesReducer);
  const cvAppList = useSelector((state) => state.cvAppReducer.cvAppList) || [];
  const [roiLoader, setRoiLoader] = useState(false);
  const [selectFloor, setSelectFloor] = useState(false);
  const roiCounterObj = useRef({});
  const [imageCache, setImageCache] = useState({}); //for storing downloaded images
  const [retryingFovIds, setRetryingFovIds] = useState([]);
  const localCache = useRef({
    current: new Map(),
    ongoingRequests: new Map(),
  });
  const pairedImages = useSelector((state) => state.imagesReducer.streamImages);


const filterCvappROisBasedOnTaggedIds = (tagId) => {
  return cvAppRois?.filter((roi) => {
    // Determine which array to use: customdata tags OR fallback to top-level tagids
    const activeTags = roi?.customdata?.statisticstagids ?? roi?.tagids;
    // Return true only if the array exists and contains the ID
    return Array.isArray(activeTags) && activeTags.includes(tagId);
  });
};

  // Helper function to check if a floor has ROIs in its areas or sub-areas
  const floorHasRoisInAreasOrSubAreas = (floor) => {
    if (!cvAppRois || !Array.isArray(cvAppRois)) {
      return false;
    }
    const areaList = floor?.nestedtags || [];
    
    return areaList?.some((area) => {
      // Check if area has ROIs
      if (!area?.id) return false;
      const areaRois = filterCvappROisBasedOnTaggedIds(area?.id);
      if (areaRois && areaRois?.length > 0) {
        return true;
      }
      
      // Check if any sub-area has ROIs
      const subAreaList = area?.nestedtags || [];
      return subAreaList?.some((subArea) => {
        if (!subArea?.id) return false;
        const subAreaRois = filterCvappROisBasedOnTaggedIds(subArea?.id);
        return subAreaRois && subAreaRois?.length > 0;
      });
    });
  };
        


  // Helper functions to check ROI availability at different levels
  const hasSiteRois = () => {
    // First check directly from cvAppRois for the selected site
    if (cvAppRois && cvAppRois.length > 0 && siteSelected?.id) {
      const roiListData = filterCvappROisBasedOnTaggedIds(siteSelected?.id);
      return roiListData && roiListData?.length > 0;
    }
    
    // Fallback: Check if sitesRoiList is already populated
    if (sitesRoiList && sitesRoiList?.length > 0) {
      return true;
    }
    
    return false;
  };
  const hasFloorRois = () => {
    // First check directly from cvAppRois for any floors
    if (cvAppRois && cvAppRois.length > 0 && floorList?.length) {
      return floorList.some((floor) => {
        const roiListData = filterCvappROisBasedOnTaggedIds(floor?.id);
        return roiListData && roiListData.length > 0;
      });
    }

    // Fallback: Check if floorRoiList is already populated
    if (floorRoiList && floorRoiList.length > 0) {
      return floorRoiList.some((floorRoi) => 
        floorRoi?.roiData && floorRoi.roiData.length > 0
      );
    }

    return false;
  };

  const hasAreaRois = () => {
    // First check directly from cvAppRois for any areas
    if (cvAppRois && cvAppRois.length > 0 && floorList?.length) {
      return floorList.some((floor) => {
        const areaList = floor?.nestedtags || [];
        return areaList.some((area) => {
          const roiListData = filterCvappROisBasedOnTaggedIds(area?.id);
          return roiListData && roiListData.length > 0;
        });
      });
    }

    // Fallback: Check if roiDatas is already populated
    if (roiDatas && roiDatas.length > 0) {
      return true;
    }

    return false;
  };

  const hasSubAreaRois = () => {
    // Check directly from cvAppRois for any sub-areas
    if (cvAppRois && cvAppRois.length > 0 && floorList?.length) {
      return floorList.some((floor) => {
        const areaList = floor?.nestedtags || [];
        return areaList.some((area) => {
          const subAreaList = area?.nestedtags || [];
          return subAreaList?.some((subArea) => {
            // Check directly from cvAppRois
            const cvAppRoifilter = filterCvappROisBasedOnTaggedIds(subArea?.id);
            return cvAppRoifilter && cvAppRoifilter?.length > 0;
          });
        });
      });
    }

    return false;
  };

  const hasRois = useMemo(() => 
    hasSiteRois() || hasFloorRois() || hasAreaRois() || hasSubAreaRois(),
    [cvAppRois, siteSelected?.id, sitesRoiList, floorList, floorRoiList, roiDatas]
  );
  
  const shouldShowAreaSubAreaRoiSelection = () => {
    const isAreaOrSubAreaLocation = selectedLocation === LOCATION_TYPES.AREA || selectedLocation === LOCATION_TYPES.SUB_AREA;
    const isEventAppType = selAppType === CV_APP_TYPE_EVENT;
    const hasAvailableRois = hasAreaRois() || hasSubAreaRois();
    
    return (isAreaOrSubAreaLocation || isEventAppType) && hasAvailableRois;
  };

  // Auto-select location based on app type and ROI availability
  useEffect(() => {
    if (cvAppRois && cvAppRois?.length > 0 && selAppType && !selectedLocation) {
      let autoSelectedLocation = "";
      
      if (selAppType === CV_APP_TYPE_EE) {
        // For entry/exit apps: check hierarchy site -> floor -> area -> subarea
        if (hasSiteRois()) {
          autoSelectedLocation = LOCATION_TYPES.SITE;
        } else if (hasFloorRois()) {
          autoSelectedLocation = LOCATION_TYPES.FLOOR;
        } else if (hasAreaRois()) {
          autoSelectedLocation = LOCATION_TYPES.AREA;
        } else if (hasSubAreaRois()) {
          autoSelectedLocation = LOCATION_TYPES.SUB_AREA;
        }
      } else if (selAppType === CV_APP_TYPE_OA) {
        // For fixed area apps: check area -> subarea
        if (hasAreaRois()) {
          autoSelectedLocation = LOCATION_TYPES.AREA;
        } else if (hasSubAreaRois()) {
          autoSelectedLocation = LOCATION_TYPES.SUB_AREA;
        }
      } else if (selAppType === CV_APP_TYPE_EVENT) {
        autoSelectedLocation = CV_APP_TYPE_EVENT;
      }
      
      if (autoSelectedLocation) {
        dispatch(
          updateRuleValue({
            propName: ROI_REDUX_PROPS.SELECTED_LOCATION,
            value: autoSelectedLocation,
          })
        );
      }
    }
  }, [cvAppRois, selAppType, selectedLocation, floorList]);
  // Update sitesRoiList when site ROIs are detected
  useEffect(() => {
    if (selectedLocation===LOCATION_TYPES.SITE && siteSelected?.id && cvAppRois && cvAppRois?.length > 0) {
      // Only update if sitesRoiList is not already populated
      if (!sitesRoiList || sitesRoiList.length === 0) {
        const roiListData = filterCvappROisBasedOnTaggedIds(siteSelected?.id);
        
        if (roiListData && roiListData.length > 0) {
          const filteredRoiList = roiListData.filter((roi) =>
            cvAppRois?.some((data) => data?.id === roi?.id)
          );
          dispatch(
            updateRuleValue({ propName: ROI_REDUX_PROPS.SITES_ROI_LIST, value: filteredRoiList })
          );
        }
      }
    }
  }, [cvAppRois, siteSelected?.id, sitesRoiList, selectedLocation]);
  
  //function to filter and remove duplicate floors
  const processFloors = (floors) => {
    if (!floors?.length) return [];
    
    const filteredFloors = (selAppType === CV_APP_TYPE_EE && selectedLocation !== LOCATION_TYPES.AREA && selectedLocation !== LOCATION_TYPES.SUB_AREA) 
      ? floors 
      : floors.filter(floorHasRoisInAreasOrSubAreas);
    
    // Remove duplicates based on floor ID
    return Array.from(new Map(filteredFloors?.map(floor => [floor?.id, floor])).values());
  };
  
  const getFloorData = async () => {
    setIsLoading(true);
    try {
      // Use Redux store if the login-time hierarchy fetch already populated it (maxdepth: 8).
      // Fall back to the API only when the store is empty (e.g. hard-refresh before saga finishes).
      const isSiteTagListLoaded = siteTagList?.sites?.length > 0;
      let floorListData;

      if (isSiteTagListLoaded) {
        // Reconstruct nested floor→area→subarea structure from the flat Redux arrays.
        floorListData = (siteTagList.floors || [])
          .filter(f => f.tagids?.includes(siteSelected?.id))
          .map(f => ({
            ...f,
            nestedtags: (siteTagList.areas || [])
              .filter(a => a.tagids?.includes(f.id))
              .map(area => ({
                ...area,
                nestedtags: (siteTagList.subareas || []).filter(s => s.tagids?.includes(area.id))
              }))
          }));
      } else {
        // Redux not yet populated — fall back to API.
        floorListData = await getTagHierarchy(
          { maxdepth: 4 },
          { id: siteSelected?.id },
          activeSession
        );
      }

      if (floorListData) {
        dispatch(
          updateRuleValue({ propName: ROI_REDUX_PROPS.FLOOR_DATA, value: floorListData })
        );
        setFloorList(processFloors(floorListData));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error("Error fetching floor list data:", error);
    }
  };

  useEffect(() => {
    if (siteSelected?.id) {
      if (!floorData?.length) {
        getFloorData();
      } 
      else {
        setFloorList(processFloors(floorData));
      }
    }
  }, [siteSelected, floorData,selectedLocation]);
  // initially select 0th floor
  useEffect(() => {
    const isNumber = (value) => {
      return typeof value === "number" && !isNaN(value);
    };
    if (!floorSelected?.id) {
      changeValue(isNumber(selectedTab) ? selectedTab : 0, ROI_REDUX_PROPS.FLOOR_SELECTED);
    }
  }, [floorList?.length]);

  useEffect(() => {
    handleRoiParentId({
      id: roiList && roiList[0]?.parentdeviceid,
    });
  }, [floorSelected, roiList]);

  const handleCloseModal = () => {
    setShowModal(false);
  };

  const updateRoiCvAppInfo = (roiItem) => {
    const appImageName = cvAppList.find(app =>
      (app?.resources?.displayNameFull && app?.resources?.displayNameFull === appSelected?.name) ||
      app?.name === appSelected?.name
    )?.image;
    const roiCvapp = roiItem?.cvapps?.find(cv => cv.image === appImageName);
    if (!roiCvapp) return;
    const appname = roiCvapp?.resources?.displayNameFull || roiCvapp.name;
    dispatch(updateRuleValue([{ propName: ROI_REDUX_PROPS.APP_SELECTED, value: {id: roiCvapp.id, name: appname} }]));
  }

  const roiCount = async (floorSelectedid) => {
    const areaList =
      (floorData?.length &&
        floorData.find((floor) => floor?.id === floorSelectedid)?.nestedtags) ||
      [];
    if (areaList && areaList?.length && selectedLocation !== LOCATION_TYPES.FLOOR) {
      setRoiLoader(true);
      const allAreaRois = [];
      for (const area of areaList) {
        // Fetch rois by tag for the current area
        const roiListData = filterCvappROisBasedOnTaggedIds(area?.id);
         

        // Filter cvAppRois based on the area rois
        const roiItem =
          roiListData &&
          roiListData.filter((roi) =>
            cvAppRois?.some((data) => data?.id === roi?.id)
          );

        if (
          roiItem &&
          roiItem?.length > 0 &&
          (selectedLocation === LOCATION_TYPES.AREA ||
            selectedLocation === LOCATION_TYPES.SUB_AREA ||
            selAppType == CV_APP_TYPE_EVENT)
        ) {
          allAreaRois.push({ id: area?.id, roiData: roiItem });
        }
      }
      // Reset before setting to avoid duplicates (reducer appends to arrays)
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_DATAS]));
      // Dispatch once with all collected area ROIs
      if (allAreaRois.length > 0) {
        dispatch(updateRuleValue({ propName: ROI_REDUX_PROPS.ROI_DATAS, value: allAreaRois }));
      }
      
      setRoiLoader(false);
    } else if (floorData && floorData?.length) {
      // Reset before setting to avoid duplicates (reducer appends to arrays)
      dispatch(resetRuleValue([ROI_REDUX_PROPS.FLOOR_ROI_LIST]));
      const allFloorRois = [];
      for (const floor of floorData) {
        const roiItem = filterCvappROisBasedOnTaggedIds(floor?.id);
         
        if (roiItem && roiItem?.length > 0 && selectedLocation === LOCATION_TYPES.FLOOR) {
          allFloorRois?.push({ id: floor?.id, roiData: roiItem });
        }
      }
      if (allFloorRois.length > 0) {
        dispatch(updateRuleValue({ propName: ROI_REDUX_PROPS.FLOOR_ROI_LIST, value: allFloorRois }));
      }
    } else {
      dispatch(
        updateRuleValue([
          { propName: ROI_REDUX_PROPS.ROI_DATAS, value: [] },
          { propName: ROI_REDUX_PROPS.FLOOR_ROI_LIST, value: [] },
        ])
      );
    }
  };

  const setSelectedtLocation = (locationType) => {
    dispatch(
      updateRuleValue({ propName: ROI_REDUX_PROPS.SELECTED_LOCATION, value: locationType })
    );
  };

  useEffect(() => {
    if (selectedLocation === LOCATION_TYPES.SITE) {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.SELECTED_TAB]));
      if (sitesRoiList?.length > 0) {
        // sending foreignid and deviceid to rule payload for aggregate
        const newRoiArr = sitesRoiList?.map((data) => ({
          deviceIds: data?.id,
          foreignIds: data?.foreignid,
          parentdeviceid: data?.parentdeviceid,
          customdata: data?.customdata,
        }));
        dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
        dispatch(
          updateRuleValue({ propName: ROI_REDUX_PROPS.ROI_SELECTED, value: newRoiArr })
        );
        updateRoiCvAppInfo(sitesRoiList[0]);
      }
    } else if (
      selectedLocation === LOCATION_TYPES.AREA ||
      selectedLocation === LOCATION_TYPES.SUB_AREA ||
      selAppType === CV_APP_TYPE_EVENT
    ) {
      const isNumber = (value) => {
        return typeof value === "number" && !isNaN(value);
      };

      dispatch(
        updateRuleValue({
          propName: ROI_REDUX_PROPS.SELECTED_TAB,
          value: isNumber(selectedTab) ? selectedTab : 0,
        })
      );
      roiCount(floorSelected?.id);
    } else if (selectedLocation === LOCATION_TYPES.FLOOR) {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.SELECTED_TAB, ROI_REDUX_PROPS.ROI_SELECTED]));
      if (selectFloor) {
        const floorRois = filterCvappROisBasedOnTaggedIds(floorSelected?.id);
        if (floorRois && floorRois.length > 0) {
          const newRoiArr = floorRois.map((roi) => ({
            deviceIds: roi?.id,
            foreignIds: roi?.foreignid,
            parentdeviceid: roi?.parentdeviceid,
            customdata: roi?.customdata,
          }));
          dispatch(updateRuleValue({ propName: ROI_REDUX_PROPS.ROI_SELECTED, value: newRoiArr }));
          updateRoiCvAppInfo(floorRois[0]);
        }
      }
      roiCount(floorSelected?.id);
    } else {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
    }
  }, [selectedLocation, sitesRoiList, floorSelected, cvAppRois, selAppType, selectedTab]);
  useEffect(() => {
    if (
      selectedLocation === LOCATION_TYPES.SITE &&
      sitesRoiList &&
      sitesRoiList?.length == 0
    ) {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED, ROI_REDUX_PROPS.SITES_ROI_LIST]));
    }
  }, [selectedLocation, sitesRoiList]);

  const changeLocation = (e) => {
    const selLocationType = e.target.value;
    dispatch(
      resetRuleValue([
        ROI_REDUX_PROPS.ROI_SELECTED,
        ROI_REDUX_PROPS.FLOOR_LIST,
        ROI_REDUX_PROPS.SITES_ROI_LIST,
      ])
    );
    setFloorDataArr([]);
    setSelectFloor(false);
    try {
      // location => floor
      if (selLocationType === LOCATION_TYPES.FLOOR && floorDataArr.length < 1) {
        // fetch floor image & count roi
        // setIsLoading(true);
        const modFloorListData = floorList.map(async (floor) => {
          const imageUrl = "";
          // await downloadFloorImage(floor?.imageid);
          const roiListData = filterCvappROisBasedOnTaggedIds(floor?.id);
            
          const filteredRoiList = roiListData?.filter((roi) =>
            cvAppRois?.some((data) => data?.id === roi?.id)
          );
          setRoiList(filteredRoiList);
          if (imageUrl || !imageUrl) {
            return {
              ...floor,
              ...{
                imageUrl,
                roiCount: filteredRoiList?.length,
                roiListData: filteredRoiList,
              },
            };
          }
        });
        Promise.all(modFloorListData)
          .then((floorDataArr) => {
            setFloorList(floorDataArr);
            // prepareFloorDataArr(floorDataArr);
          })
          .finally(() => {
            setIsLoading(false);
          });
      } // location => site
      else if (
        selLocationType === LOCATION_TYPES.SITE &&
        (sitesRoiList?.length < 1 || !sitesRoiList)
      ) {
        // count roi for selected site
        setIsLoading(true);
        const roiListData = filterCvappROisBasedOnTaggedIds(siteSelected?.id);
          
        const filteredRoiList = roiListData?.filter((roi) =>
          cvAppRois?.some((data) => data?.id === roi?.id)
        );
        setSiteRoiList(filteredRoiList);
        dispatch(
          updateRuleValue({ propName: ROI_REDUX_PROPS.SITES_ROI_LIST, value: filteredRoiList })
        );
        setRoiList(filteredRoiList);
        setIsLoading(false);
      }
      setSelectedtLocation(selLocationType);
    } catch (err) {
      console.log("Error:", err);
    }
  };
  const changeValue = (idx, attrName, clear) => {
    if (attrName === ROI_REDUX_PROPS.FLOOR_SELECTED) {
      selectedLocation &&
        selectedLocation?.length > 0 &&
        roiCount(floorList[idx]?.id);
      dispatch(
        updateRuleValue({
          propName: attrName,
          value: { id: floorList[idx]?.id, name: floorList[idx]?.name },
        })
      );
      roiCounterObj.current = {};
      setTrigger(false);

      if (clear === "reset") {
        // dispatch(resetRuleValue({ propName: "areaSelected" }));
        // dispatch(resetRuleValue({ propName: 'subAreaSelected' }));
        dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
      }
    }
  };

  const handleTabChange = (event, newValue) => {
    dispatch(updateRuleValue({ propName: ROI_REDUX_PROPS.SELECTED_TAB, value: newValue }));
    changeValue(newValue, ROI_REDUX_PROPS.FLOOR_SELECTED, "reset");
  };
  const onAreaChanged = (areaId, areaName) => {
    dispatch(
      updateRuleValue({
        propName: ROI_REDUX_PROPS.AREA_SELECTED,
        value: { id: areaId, name: areaName },
      })
    );
    dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED, ROI_REDUX_PROPS.SUB_AREA_SELECTED]));
  };
  const onFloorChanged = (floorId, floorName) => {
    dispatch(
      updateRuleValue({
        propName: ROI_REDUX_PROPS.FLOOR_SELECTED,
        value: { id: floorId, name: floorName },
      })
    );
    dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
  };
  const onSubAreaChanged = (subAreaId, subAreaName) => {
    dispatch(
      updateRuleValue({
        propName: ROI_REDUX_PROPS.SUB_AREA_SELECTED,
        value: { id: subAreaId, name: subAreaName },
      })
    );
    dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
  };

  const selectRoi = (roiItem) => {
    dispatch(updateRuleValue([
      { propName: ROI_REDUX_PROPS.SELECT_AREA, value: false },
      { propName: ROI_REDUX_PROPS.CHECKED_AREA_ROI, value: true },
      { propName: ROI_REDUX_PROPS.CHECKED_SUB_AREA_ROI, value: false },
      { propName: ROI_REDUX_PROPS.SELECT_SUB_AREA, value: false },
    ]));
    const newRoiArr = [
      {
        id: roiItem?.id,
        parentdeviceid: roiItem?.parentdeviceid,
        customdata: roiItem?.customdata,
      },
    ];
    dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
    dispatch(updateRuleValue([{ propName: ROI_REDUX_PROPS.ROI_SELECTED, value: newRoiArr }]));
    updateRoiCvAppInfo(roiItem);
  };

  const selectSubAreaRoi = (roiItem) => {
    dispatch(updateRuleValue([
      { propName: ROI_REDUX_PROPS.SELECT_SUB_AREA, value: false },
      { propName: ROI_REDUX_PROPS.CHECKED_SUB_AREA_ROI, value: true },
      { propName: ROI_REDUX_PROPS.CHECKED_AREA_ROI, value: false },
    ]));
    const newRoiArr = [
      {
        id: roiItem?.id,
        parentdeviceid: roiItem?.parentdeviceid,
        customdata: roiItem?.customdata,
      },
    ];
    dispatch(resetRuleValue([ROI_REDUX_PROPS.AREA_SELECTED, ROI_REDUX_PROPS.ROI_SELECTED]));
    dispatch(updateRuleValue([{ propName: ROI_REDUX_PROPS.ROI_SELECTED, value: newRoiArr }]));
    updateRoiCvAppInfo(roiItem);
  };

  const updateRoionAreaLevel = (roiItem) => {
    dispatch(updateRuleValue([
      { propName: ROI_REDUX_PROPS.SELECT_AREA, value: true },
      { propName: ROI_REDUX_PROPS.SELECT_SUB_AREA, value: false },
      { propName: ROI_REDUX_PROPS.CHECKED_AREA_ROI, value: false },
    ]));
    // setSelectFloor(false);
    const newRoiArr = roiItem[0]?.roiData?.map((data) => ({
      deviceIds: data?.id,
      foreignIds: data?.foreignid,
      parentdeviceid: data?.parentdeviceid,
      customdata: data?.customdata,
    }));
    dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
    dispatch(updateRuleValue({ propName: ROI_REDUX_PROPS.ROI_SELECTED, value: newRoiArr }));
    updateRoiCvAppInfo(roiItem[0]?.roiData[0]);
    
    if (!roiItem || roiItem?.length === 0) {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
    }
  };
  const updateRoionFloorLevel = (roiItem) => {
    setSelectFloor(true);
    const newRoiArr =
      roiItem &&
      roiItem[0]?.roiData?.map((data) => ({
        deviceIds: data?.id,
        foreignIds: data?.foreignid,
        parentdeviceid: data?.parentdeviceid,
        customdata: data?.customdata,
      }));
    if (selectArea) {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
      dispatch(updateRuleValue({ propName: ROI_REDUX_PROPS.ROI_SELECTED, value: newRoiArr }));
      updateRoiCvAppInfo(roiItem[0]?.roiData[0]);
    }
    if (!roiItem || roiItem?.length === 0) {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
    }
  };

  const updateRoionSubAreaLevel = (roiItem) => {
    dispatch(
      updateRuleValue([
        { propName: ROI_REDUX_PROPS.SELECT_SUB_AREA, value: true },
        { propName: ROI_REDUX_PROPS.SELECTED_LOCATION, value: LOCATION_TYPES.SUB_AREA },
        { propName: ROI_REDUX_PROPS.SELECT_AREA, value: false },
        { propName: ROI_REDUX_PROPS.CHECKED_SUB_AREA_ROI, value: false },
      ])
    );
    // setSelectFloor(false);
    const newRoiArr = roiItem?.map((data) => ({
      deviceIds: data?.id,
      foreignIds: data?.foreignid,
      parentdeviceid: data?.parentdeviceid,
      customdata: data?.customdata,
    }));
    if (newRoiArr && newRoiArr.length > 0) {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
      dispatch(updateRuleValue({ propName: ROI_REDUX_PROPS.ROI_SELECTED, value: newRoiArr }));
      updateRoiCvAppInfo(roiItem[0]);
    } else {
      dispatch(resetRuleValue([ROI_REDUX_PROPS.ROI_SELECTED]));
    }
  };

  const isAccordionOpen = (visibleList, selectedId, itemId) => {
    if (roiSelected?.length && selectedId === itemId) return true;
    if (visibleList?.length === 1) return true;
    if (!roiSelected?.length && visibleList?.[0]?.id === itemId) return true;
    return false;
  };

  const isSelectedRoi = (selRoiId) => {
    const roiIds = roiSelected && roiSelected?.map((item) => item?.id);
    return roiIds?.includes(selRoiId);
  };

  // Returns: a url string on success, null once the fetch has failed, or
  // undefined while no result has come back yet (still loading).
  const getRoiImgUrl = (roi) => {
    const fovId = roi?.parentdeviceid;
    if (retryingFovIds.includes(fovId)) {
      return undefined;
    }
    const pairedImage =
      pairedImages && pairedImages.find((img) => img?.id === fovId);
    if (pairedImage) {
      return pairedImage?.imageUrl;
    } else if (roiImages && roiImages[fovId]) {
      return roiImages[fovId];
    } else if (!localCache.current.current.has(fovId)) {
      const fovDetails = fovList && fovList?.find((fov) => fov.id === fovId);
      if (fovDetails) {
        fetchFloorImage(fovId, fovDetails?.customdata?.imageid).then(() => {
          setTrigger((prev) => !prev);
        });
      }
      return undefined;
    } else {
      return localCache.current.current.get(fovId);
    }
  };

  const retryFloorImage = (fovId) => {
    localCache.current.current.delete(fovId);
    localCache.current.ongoingRequests.delete(fovId);
    setRetryingFovIds((prev) => [...prev, fovId]);
    const fovDetails = fovList && fovList?.find((fov) => fov.id === fovId);
    if (fovDetails) {
      fetchFloorImage(fovId, fovDetails?.customdata?.imageid).then(() => {
        setRetryingFovIds((prev) => prev.filter((id) => id !== fovId));
        setTrigger((prev) => !prev);
      });
    } else {
      setRetryingFovIds((prev) => prev.filter((id) => id !== fovId));
    }
  };
  const fetchFloorImage = async (fovId, imageId) => {
    try {
      // Check if image already exists in cache
      if (localCache.current.current.has(fovId)) {
        return localCache.current.current.get(fovId);
      }

      // Check if a request for this fovId is already ongoing
      if (localCache.current.ongoingRequests.has(fovId)) {
        return localCache.current.ongoingRequests.get(fovId);
      }
      const requestPromise = ImageDownloadMedia(imageId, activeSession, '', "snapshot")
        .then((url) => {
          localCache.current.current.set(fovId, url);
          const updatedCache = { ...imageCache, [fovId]: url };
          setImageCache(updatedCache);
          if (updatedCache) {
            dispatch(
              updateRuleValue({
                propName: ROI_REDUX_PROPS.ROI_IMAGES,
                value: updatedCache,
              })
            );
          }
          localCache.current.ongoingRequests.delete(fovId);
          setImageLoading(false);
          setFloorImageLoading(false);
          return url;
        })
        .catch((error) => {
          console.error("Error downloading image:1", error);
          localCache.current.current.set(fovId, null);
          localCache.current.ongoingRequests.delete(fovId);
          dispatch(
            updateRuleValue({
              propName: ROI_REDUX_PROPS.ROI_IMAGES,
              value: { [fovId]: null },
            })
          );
          setImageLoading(false);
          setFloorImageLoading(false);
        })
        .finally(() => {
          setIsLoading(false);
          setFloorImageLoading(false);
          //  loading set to false in case of error
        });

      // Storing the ongoing request
      localCache.current.ongoingRequests.set(fovId, requestPromise);
      setImageLoading(true);
      setFloorImageLoading(true);

      // Return the promise for the ongoing request
      return requestPromise;
    } catch (error) {
      setImageLoading(false);
      console.error("Error downloading image:2", error);
      setFloorImageLoading(false);
      localCache.current.current.set(fovId, null);
      dispatch(
        updateRuleValue({
          propName: ROI_REDUX_PROPS.ROI_IMAGES,
          value: { [fovId]: null },
        })
      );
      return null; // Return null to indicate failure
    }
  };

  const showRoiList = (rois, type, matchId = 0, parentName = "") => {
    const renderNumberInputField = (rois, type, matchId) => {
      const hasMatchingTagId =
        rois && rois?.some((roi) => roi?.tagids?.includes(matchId));

      if (
        (appSelected?.name === CVAPP_PARKINGOCCUPANCYOA ||
          appSelected?.name === CVAPP_PARKINGOCCUPANCYEE) &&
        hasMatchingTagId
      ) {
        return (
          <div className="input-field">
            <ParkingField
              tagAllLists={tagAllLists}
              matchId={matchId}
              type={type}
            />
          </div>
        );
      }
      return null;
    };

    return (
      <>
        {renderNumberInputField(rois, type, matchId)}
        {rois &&
          rois?.map((roi, index) => {
            if (type !== "common" && roi?.tagids?.includes(matchId)) {
              const imgUrl = getRoiImgUrl(roi);
              return (
                <>
                  <div className="image-block" key={roi.id}>
                    <div className="chkbox_conatiner">
                          <div>
                            <ButtonIcon
                              kind="lowContrast"
                              size="small"
                              surfaceType="colorFill"
                              state="default"
                              onClick={() => {
                                setShowModal(true);
                                setSelectedRoi(roi?.id);
                              }}
                              data-testid={CREATERULE_TEST_IDS.ROI_ALERTS_BUTTON}
                              renderIcon={() => (
                                <Icon name="reports-and-alerts" size="small" />
                              )}
                            />
                            {showModal && selectedRoi === roi?.id && (
                              <div>
                                <RuleListModal
                                  id={selectedRoi}
                                  onClose={handleCloseModal}
                                />
                              </div>
                            )}
                          </div>
                      {imgUrl && selAppType !== CV_APP_TYPE_EE &&
                        appSelected?.name !== CVAPP_PARKINGOCCUPANCYOA && (
                          <div className="parking-roi-image">
                            {type == "area" ? (
                              <input
                                type="radio"
                                value={roi?.id}
                                checked={
                                  isSelectedRoi(roi?.id) &&
                                  checkedAreaRoi &&
                                  !checkedSubAreaRoi &&
                                  !selectSubArea
                                }
                                onChange={() => {
                                      onAreaChanged(matchId, parentName);
                                  selectRoi(roi);
                                }}
                                data-testid={CREATERULE_TEST_IDS.ROI_SELECT_RADIO}
                              />
                            ) : (
                              <input
                                type="radio"
                                value={roi?.id}
                                checked={
                                  isSelectedRoi(roi?.id) &&
                                  checkedSubAreaRoi &&
                                  !checkedAreaRoi
                                }
                                onChange={() => {
                                  onSubAreaChanged(matchId, parentName);
                                  selectSubAreaRoi(roi);
                                }}
                                data-testid={CREATERULE_TEST_IDS.SUBAREA_ROI_SELECT_RADIO}
                              />
                            )}
                          </div>
                        )}
                    </div>
                    <div>
                      {imgUrl ? (
                        <DisplayCanvas
                          videoSource={imgUrl || ""}
                          imageSize={{ width: 168.23, height: 108 }}
                          initialPolygons={transformPolygonData(roi?.customdata)}
                          originalSize={[roi?.customdata, roi?.customdata]}
                        />
                      ) : imgUrl === null ? (
                          <div className="alert-snapshot-error">
                          <div className="camera-snapshot-error-content">
                            <Icon name="error" size="small" color="#6f7171" />
                            <LightText>Snapshot failed to load</LightText>
                            <ButtonIcon
                              onClick={() => retryFloorImage(roi?.parentdeviceid)}
                              renderIcon={() => <Icon name="sync" size="small" />}
                            />
                          </div>
                        </div>
                      ) : ( 
                            <div className="alert-snapshot-loading">
                              <div className="camera-snapshot-loader-wrapper">
                                <Loader active={true} fullscreen={false} surface="light" />
                              </div>
                            </div>
                      )}
                      <br />
                    </div>
                  </div>
                </>
              );
            }
          })}
      </>
    );
  };

  const showAccordion = (selFloorId, roiDatas) => {
    if (floorData?.length < 1) return null;

    const areaList =
      floorData &&
      floorData?.length &&
      floorData.find((floor) => floor?.id === selFloorId)?.nestedtags;

    const renderFloorAccordion = () => {
      const getFloorRoifilter = (floor) =>
        floorRoiList &&
        floorRoiList
          .find((floorRoi) => floorRoi?.id === floor?.id)
          ?.roiData?.filter((roi) => roi?.tagids?.includes(floor?.id));

      const hasAnyRois = floorData?.some((floor) => {
        const floorRoifilter = getFloorRoifilter(floor);
        return floorRoifilter && floorRoifilter.length > 0;
      });

      // If no ROIs exist for any floor, show the message
      if (!hasAnyRois) {
        return (
          <div>
            <Body size="large" color="#A7A7A7">
              {SELECT_ROI_CONSTANTS.NO_ROI_FLOORS}
            </Body>
          </div>
        );
      }

      const visibleFloors = floorList?.filter((floor) => {
        const floorRoifilter = getFloorRoifilter(floor);
        return floorRoifilter && floorRoifilter.length > 0;
      });

      return (
        <Accordion topLine={false}>
          {visibleFloors?.map((floor, floorIdx) => {
              const floorRoifilter = getFloorRoifilter(floor);
              return (
                <AccordionItem
                  opened={isAccordionOpen(visibleFloors, floorSelected?.id, floor?.id)}
                  key={floor?.id}
                  topLine={false}
                >
                  <AccordionHeader>
                    <div className="accordion-alignment">
                      {selAppType !== CV_APP_TYPE_EVENT ? (
                        <input
                          className="roi-select-radio"
                          type="radio"
                          name={`floor_name_${floorIdx}`}
                          value={floor?.id}
                          checked={
                            floor?.id === floorSelected?.id &&
                            selectFloor &&
                            roiSelected?.length
                          }
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => {
                            onFloorChanged(floor?.id, floor?.name);
                            updateRoionFloorLevel(floorRoifilter);
                            setSelectFloor(true);
                          }}
                          selected={
                            selectFloor &&
                            roiSelected &&
                            roiSelected?.length &&
                            floorSelected?.id === floor?.id
                          }
                        />
                      ) : (
                        ""
                      )}
                      <AccordionTitle>
                        <Typography
                          className={`sasp_font-size-16 sasp_font-weight-400`}
                        >
                          {floor?.name}
                        </Typography>
                      </AccordionTitle>
                    </div>

                    <AccordionSubTitle>
                      <span id={`floor_count_${floorIdx}`}>
                        {(floorRoifilter && floorRoifilter?.length) || 0}
                      </span>{" "}
                      {SELECT_ROI_CONSTANTS.REGIONS_OF_INTEREST}
                    </AccordionSubTitle>
                </AccordionHeader>

                  <AccordionDetail>
                    <div className="sel-roi-image-grid">
                      {/* ROI list for corresponding Floor */}
                      {showRoiList(
                        floorRoifilter,
                        "floor",
                        floor?.id,
                        floor?.name
                      )}
                    </div>
                  </AccordionDetail>
                </AccordionItem>
              );
            })}
        </Accordion>
      );
    };

    const renderAreaAccordion = () => {
      const getAreaRoifilter = (area) =>
        roiDatas && roiDatas.filter((obj) => obj.id === area?.id);

      const visibleAreas = areaList?.filter((area) => {
        const roifilter = getAreaRoifilter(area);
        return roifilter && roifilter.length > 0;
      });

      return (
        <Accordion topLine={false}>
          {visibleAreas?.map((area, areaIdx) => {
            const roifilter = getAreaRoifilter(area);
            return (
              <AccordionItem
                topLine={false}
                opened={isAccordionOpen(visibleAreas, areaSelected?.id, area?.id)}
                key={area?.id}
              >
                <AccordionHeader>
                  <AccordionTitle>
                    {selAppType !== CV_APP_TYPE_EVENT ? (
                      <input
                        className="roi-select-radio"
                        type="radio"
                        name={`area_name_${areaIdx}`}
                        value={area?.id}
                        checked={
                          !selectSubArea &&
                          area?.id === areaSelected?.id &&
                          selectArea &&
                          roiSelected?.length
                        }
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => {
                          onAreaChanged(area?.id, area?.name);
                          updateRoionAreaLevel(roifilter);
                          dispatch(updateRuleValue([
                            { propName: ROI_REDUX_PROPS.SELECT_AREA, value: true },
                            { propName: ROI_REDUX_PROPS.CHECKED_AREA_ROI, value: false },
                          ]));
                        }}
                        data-testid={CREATERULE_TEST_IDS.AREA_SELECT_RADIO}
                        selected={
                          (!selectSubArea && selectArea) ||
                          (roiSelected &&
                            roiSelected?.length &&
                            areaSelected?.id === area?.id)
                        }
                      />
                    ) : (
                      ""
                    )}
                    <Typography
                      className={`sasp_font-size-16 sasp_font-weight-400`}
                    >
                      {area?.name}
                    </Typography>
                  </AccordionTitle>
                  <AccordionSubTitle>
                    <span id={`area_count_${areaIdx}`}>
                      {roifilter[0]?.roiData?.length || 0}
                    </span>{" "}
                    {SELECT_ROI_CONSTANTS.REGIONS_OF_INTEREST}
                  </AccordionSubTitle>
                </AccordionHeader>
                <AccordionDetail>
                  <div className="sel-roi-image-grid">
                    {showRoiList(
                      roifilter[0]?.roiData,
                      "area",
                      area?.id,
                      area?.name
                    )}
                  </div>
                </AccordionDetail>
              </AccordionItem>
            );
          })}
        </Accordion>
      );
    };

    const renderSubAreaAccordion = () => {
      const getValidSubAreas = (area) =>
        area?.nestedtags?.filter((subArea) => {
          const subAreaRoifilter = filterCvappROisBasedOnTaggedIds(subArea?.id);
          return subAreaRoifilter && subAreaRoifilter.length > 0;
        });

      const allVisibleSubAreas = areaList?.flatMap(
        (area) => getValidSubAreas(area) || []
      );

      return (
        <Accordion topLine={false}>
          {areaList?.map((area) => {
            const validSubAreas = getValidSubAreas(area);
            if (validSubAreas?.length > 0) {
              return (
                <div key={area?.id}>
                  <Body size="large" bold>
                    <br />
                    {area?.name}
                  </Body>

                  {validSubAreas?.map((subArea, subAreaIdx) => {
                  const subAreaRoifilter = filterCvappROisBasedOnTaggedIds(subArea?.id);
                  

                    return (
                      <AccordionItem
                        topLine={false}
                        opened={isAccordionOpen(allVisibleSubAreas, subAreaSelected?.id, subArea?.id)}
                        key={subArea?.id}
                      >
                        <AccordionHeader>
                          <AccordionTitle>
                            {selAppType !== CV_APP_TYPE_EVENT ? (
                              <input
                                className="roi-select-radio"
                                type="radio"
                                name={`subarea_name_${subAreaIdx}`}
                                value={subArea?.id}
                                checked={
                                  !selectArea &&
                                  subArea?.id === subAreaSelected?.id &&
                                  selectSubArea &&
                                  roiSelected?.length
                                }
                                selected={
                                  (!selectArea && selectSubArea) ||
                                  (roiSelected &&
                                    roiSelected?.length &&
                                    subAreaSelected?.id === subArea?.id)
                                }
                                onClick={(e) => e.stopPropagation()}
                                onChange={() => {
                                  onSubAreaChanged(subArea?.id, subArea?.name);
                                  updateRoionSubAreaLevel(subAreaRoifilter);
                                }}
                                data-testid={CREATERULE_TEST_IDS.SUBAREA_SELECT_RADIO}
                              />
                            ) : (
                              ""
                            )}
                            <Typography
                              className={`sasp_font-size-16 sasp_font-weight-400`}
                            >
                              {subArea?.name}
                            </Typography>
                          </AccordionTitle>
                          <AccordionSubTitle>
                            <span id={`subarea_count_${subAreaIdx}`}>
                              {subAreaRoifilter?.length || 0} {SELECT_ROI_CONSTANTS.REGIONS_OF_INTEREST}
                            </span>
                          </AccordionSubTitle>
                        </AccordionHeader>

                        <AccordionDetail>
                          <div className="sel-roi-image-grid">
                            {subAreaRoifilter &&
                              subAreaRoifilter?.length > 0 &&
                              showRoiList(
                                subAreaRoifilter,
                                "sub-area",
                                subArea?.id,
                                subArea?.name
                              )}
                          </div>
                        </AccordionDetail>
                      </AccordionItem>
                    );
                  })}
                </div>
              );
            }

            return null;
          })}
        </Accordion>
      );
    };

    const renderAccordionforEventApp = () => {
      return (
        <Accordion topLine={false}>
          {areaList?.map((area, areaIdx) => {
            const areaRoifilter =
              roiDatas && roiDatas.filter((obj) => obj.id === area?.id);
            return (
              <>
                {areaRoifilter && areaRoifilter?.length > 0 ? (
                  <AccordionItem 
                    topLine={false}
                    opened={isAccordionOpen(roiDatas, areaSelected?.id, area?.id)}
                    key={area?.id}
                  >
                    <AccordionHeader>
                      <AccordionTitle>
                        <Typography
                          className={`sasp_font-size-16 sasp_font-weight-400`}
                        >
                          {area?.name}
                        </Typography>
                      </AccordionTitle>
                      <AccordionSubTitle>
                        <span id={`area_count_${areaIdx}`}>
                          {areaRoifilter[0]?.roiData?.length || 0}
                        </span>{" "}
                        {SELECT_ROI_CONSTANTS.REGIONS_OF_INTEREST}
                      </AccordionSubTitle>
                    </AccordionHeader>
                    <AccordionDetail>
                      <div className="sel-roi-image-grid">
                        {showRoiList(
                          areaRoifilter[0]?.roiData,
                          "area",
                          area?.id,
                          area?.name
                        )}
                      </div>
                    </AccordionDetail>
                  </AccordionItem>
                ) : (
                  ""
                )}
              </>
            );
          })}
        </Accordion>
      );
    };

    return (
      <>
        {selectedLocation === LOCATION_TYPES.FLOOR && renderFloorAccordion()}
        {selectedLocation === LOCATION_TYPES.AREA && renderAreaAccordion()}
        {selectedLocation === LOCATION_TYPES.SUB_AREA && renderSubAreaAccordion()}
        {selAppType === CV_APP_TYPE_EVENT && renderAccordionforEventApp()}
      </>
    );
  };

  return (
    <>
      <div className="create-rule-common-container">
        <div>
          <Typography className="bulk-rule-content-header-text">
            {SELECT_ROI_CONSTANTS.HEADER_TEXT}
          </Typography>

          {selAppType === CV_APP_TYPE_EE && hasRois ? (            
            <div className="location-selection">
              <DropdownSelect
                label="Location"
                onChange={changeLocation}
                data-testid={CREATERULE_TEST_IDS.LOCATION_DROPDOWN}
                width="100%"
              >
                {[
                  hasSiteRois() && (
                    <option key="site" value={LOCATION_TYPES.SITE} selected={LOCATION_TYPES.SITE === selectedLocation}>
                      {SELECT_ROI_CONSTANTS.LOCATION_SITE}
                    </option>
                  ),
                  hasFloorRois() && (
                    <option key="floor" value={LOCATION_TYPES.FLOOR} selected={LOCATION_TYPES.FLOOR === selectedLocation}>
                      {SELECT_ROI_CONSTANTS.LOCATION_FLOOR}
                    </option>
                  ),
                  hasAreaRois() && (
                    <option key="area" value={LOCATION_TYPES.AREA} selected={LOCATION_TYPES.AREA === selectedLocation}>
                      {SELECT_ROI_CONSTANTS.LOCATION_AREA}
                    </option>
                  ),
                  hasSubAreaRois() && (
                    <option
                      key="sub-area"
                      value={LOCATION_TYPES.SUB_AREA}
                      selected={LOCATION_TYPES.SUB_AREA === selectedLocation}
                    >
                      {SELECT_ROI_CONSTANTS.LOCATION_SUBAREA}
                    </option>
                  ),
                ].filter(Boolean)}
              </DropdownSelect>
            </div>
          ) : (
          <div>
            <br />
            {selAppType === CV_APP_TYPE_EE && !isLoading && !roiLoader && (
            <Body size="large" color="#A7A7A7">
              {SELECT_ROI_CONSTANTS.NO_ROI_ENTRY_EXIT}
            </Body>
            )}
          </div>
          )}
          {selAppType === CV_APP_TYPE_OA && (hasAreaRois() || hasSubAreaRois()) ? (
            <div className="location-selection">
              <DropdownSelect
                label="Location"
                onChange={changeLocation}
                data-testid={CREATERULE_TEST_IDS.FIXEDAREA_LOCATION_DROPDOWN}
                width="100%"
              >
                {[
                  hasAreaRois() && (
                    <option key="area" value={LOCATION_TYPES.AREA} selected={LOCATION_TYPES.AREA === selectedLocation}>
                      {SELECT_ROI_CONSTANTS.LOCATION_AREA}
                    </option>
                  ),
                  hasSubAreaRois() && (
                    <option
                      key="sub-area"
                      value={LOCATION_TYPES.SUB_AREA}
                      selected={LOCATION_TYPES.SUB_AREA === selectedLocation}
                    >
                      {SELECT_ROI_CONSTANTS.LOCATION_SUBAREA}
                    </option>
                  ),
                ].filter(Boolean)}
              </DropdownSelect>
            </div>
          ) : (
          <div>
            <br />
            {selAppType === CV_APP_TYPE_OA && !isLoading && !roiLoader && (
            <Body size="large" color="#A7A7A7">
              {SELECT_ROI_CONSTANTS.NO_ROI_FIXED_AREA}
            </Body>
            )}
          </div>
          )}
          {!imageLoading && selectedLocation === CV_APP_TYPE_EVENT && !hasAreaRois() && !hasSubAreaRois() && (
            <div> 
              <Body size="large" color="#A7A7A7">
                {SELECT_ROI_CONSTANTS.NO_ROI_EVENTAPP}
              </Body>
            </div>
          )}
        </div>

        <div
          className={`${
            selectedLocation == LOCATION_TYPES.SITE 
              ? "" 
              : (shouldShowAreaSubAreaRoiSelection() && floorList && floorList?.length > 0)
              ? "sel-roi-content"
              : ""
          }`}
        >
          {/* location > site */}
          {selectedLocation === LOCATION_TYPES.SITE && (
            <>
              <div className="location-site-list">
                <RadioButtonGroup>
                  <RadioButton
                    label={siteSelected?.name}
                    children={SELECT_ROI_CONSTANTS.getApplyAlertToSiteMessage(
                      sitesRoiList?.length || 0,
                      appSelected?.name
                    )}
                    value={siteSelected?.id}
                    selected={sitesRoiList && sitesRoiList?.length > 0}
                  />
                </RadioButtonGroup>
              </div>
              <br />
              <div
                className={`${
                  sitesRoiList?.length ? "sel-roi-image-grid" : ""
                }`}
              >
                {sitesRoiList?.length &&
                  showRoiList(
                    sitesRoiList,
                    "site",
                    siteSelected?.id,
                    siteSelected?.name
                  )}
              </div>
            </>
          )}
          {selectedLocation === LOCATION_TYPES.FLOOR && (
            <div className="input-field change">
              {floorSelected?.id && showAccordion(floorSelected?.id, roiDatas)}
            </div>
          )}{" "}
          {/* (app type > open_area || event) || location > area */}
          {shouldShowAreaSubAreaRoiSelection() ? (
            <>
              {floorList && floorList?.length > 0 ? (
                <>
                  {/* left floor list */}
                  <div className="sel-roi-left">
                    {(selectedLocation !== "" || selAppType == CV_APP_TYPE_EVENT) && (
                      <Tabs
                        orientation="vertical"
                        indicatorPosition="bottom"
                        width="auto"
                        onTabChange={handleTabChange}
                      >
                        {floorList?.map((floor, index) => (
                          <Tab
                            key={index}
                            uniqueId="breakWord"
                            label={floor?.name}
                            selected={floor?.id === floorSelected?.id}
                          />
                        ))}
                      </Tabs>
                    )}
                  </div>

                  {/* right area / sub-area / roi list accordion */}
                  <div className="sel-roi-right">
                    {floorSelected &&
                      floorSelected?.id &&
                      showAccordion(floorSelected?.id, roiDatas)}
                  </div>
                </>
              ) : (
                !isLoading && ( 
                  <div>
                    <Body size="large" color="#A7A7A7">
                      {selAppType === CV_APP_TYPE_EVENT
                        ? SELECT_ROI_CONSTANTS.NO_ROI_EVENTAPP 
                        : (SELECT_ROI_CONSTANTS.NO_ROI_FIXED_AREA)}
                    </Body>
                  </div>
                )
              )}
            </>
          ) : (
            ""
          )}
        </div>
        {(isLoading || roiLoader) && (
          <div className="loader-absolute-container">
            <Loader fullscreen={false} active={isLoading || roiLoader} />
          </div>
        )}
      </div>
    </>
  );
};

export default SelectROI;
